import { gql, TypedDocumentNode } from "@apollo/client";

import {
  CustomerPropertiesQueryQuery,
  CustomerPropertiesQueryQueryVariables,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { elementTypeFragment } from "src/graphql/anti-fraud/fragments/ElementTypeFragment";
import { propertyFragment } from "src/graphql/anti-fraud/fragments/PropertyFragment";
import { useFetchQuery } from "src/graphql/utils";
import { mapToPropertyCode } from "src/mappers/property.mappers";
import { mapDataTypeToAppDataType } from "src/mappers/typedValue.mappers";
import { ClientSchemaProperty } from "src/types/clientSchema";

export const customerPropertiesQuery = gql /* GraphQL */ `
  query CustomerPropertiesQuery {
    schema {
      customerType {
        name
        code
        properties {
          ...PropertyFragment
        }
        elements {
          name
          code
          elementType {
            ...ElementTypeFragment
          }
        }
      }
    }
  }
  ${propertyFragment}
  ${elementTypeFragment}
` as TypedDocumentNode<CustomerPropertiesQueryQuery, CustomerPropertiesQueryQueryVariables>;

export const useGetCustomerPropertiesQuery = ({
  onError,
  skip,
}: {
  onError: (error: Error) => void;
  skip?: boolean;
}) => {
  return useFetchQuery(customerPropertiesQuery, {
    toData: (data) => [
      ...(data?.schema?.customerType?.properties.map(
        (property) =>
          ({
            propertyCode: mapToPropertyCode(property.code),
            propertyName: property.name,
            dataType: mapDataTypeToAppDataType(property.datatype),
            mandatory: property.mandatory,
            elementCode: null,
            prefix: data?.schema?.customerType?.name || "",
            clientType: "customer",
          }) satisfies ClientSchemaProperty
      ) ?? []),
      ...(data?.schema?.customerType?.elements.flatMap((element) =>
        element.elementType.properties.map(
          (property) =>
            ({
              propertyCode: mapToPropertyCode(property.code),
              propertyName: property.name,
              dataType: mapDataTypeToAppDataType(property.datatype),
              mandatory: property.mandatory,
              elementCode: element.elementType.code,
              prefix: `${data?.schema?.customerType?.name ?? ""} - ${element.elementType.name}`,
              clientType: "customer",
            }) satisfies ClientSchemaProperty
        )
      ) ?? []),
    ],
    isEmpty: (data) => !data?.schema?.customerType,
    queryOptions: { onError, skip },
    uiErrors: {
      fetchError: "FETCH-IDENTIFIER-TYPES-ERROR",
      emptyError: "EMPTY-IDENTIFIER-TYPES-ERROR",
    },
  });
};
